<template>
    <div class="assign-account-lookup">
        <v-text-field
            v-model="search"
            label="Search name, account ID, phone, or address"
            placeholder="Type at least 2 characters..."
            prepend-icon="mdi-magnify"
            hide-details
            clearable
            @input="onSearch"
        ></v-text-field>

        <div v-if="!selected && loading" class="grey--text mt-2">Searching accounts...</div>
        <div
            v-else-if="!selected && search && search.length >= 2 && !results.length"
            class="grey--text mt-2"
        >
            No matching accounts
        </div>

        <div v-if="!selected && results.length" class="assign-account-results mt-2">
            <div class="caption grey--text mb-1">
                {{ results.length }} result{{ results.length === 1 ? "" : "s" }}
            </div>
            <v-card
                v-for="account in results"
                :key="account.accountID"
                class="assign-account-card pa-2 mb-2"
                :class="{
                    'assign-account-card--selected':
                        selected && selected.accountID === account.accountID,
                }"
                @click.native="select(account)"
            >
                <v-layout row>
                    <v-icon
                        class="assign-account-card-icon mr-3"
                        color="#90a4ae"
                        size="34"
                    >
                        mdi-account-circle
                    </v-icon>
                    <div class="assign-account-card-body">
                        <div class="font-weight-medium">
                            {{ account.name || "—" }}
                            <span
                                v-if="account.hname"
                                class="grey--text font-weight-regular"
                            >
                                · {{ account.hname }}
                            </span>
                        </div>
                        <div class="caption mt-1">
                            <span class="font-weight-medium">{{ account.accountID }}</span>
                            <span v-if="account.homePhone">
                                · {{ formatPhone(account.homePhone) }}
                            </span>
                            <span v-if="account.cellPhone">
                                · {{ formatPhone(account.cellPhone) }}
                            </span>
                        </div>
                        <div v-if="account.address1" class="caption grey--text">
                            {{ account.address1 }}
                        </div>
                    </div>
                </v-layout>
            </v-card>
        </div>

        <v-card
            v-if="selected && selected.accountID"
            class="assign-account-selected mt-3"
        >
            <div class="assign-account-selected-header">
                <span class="assign-account-selected-label">Selected account</span>
                <v-btn
                    icon
                    small
                    class="assign-account-selected-clear my-0 py-0"
                    title="Remove selected account"
                    @click.stop="removeSelected"
                >
                    <v-icon small color="#546e7a">mdi-close</v-icon>
                </v-btn>
            </div>
            <div class="assign-account-selected-main">
                <v-icon
                    class="assign-account-selected-icon"
                    color="#4caf50"
                    size="36"
                >
                    mdi-account-circle
                </v-icon>
                <div class="assign-account-selected-body">
                    <div class="assign-account-selected-name">
                        {{ selected.name || selected.accountID }}
                        <span v-if="selected.hname" class="assign-account-selected-he">
                            {{ selected.hname }}
                        </span>
                    </div>
                    <div class="assign-account-selected-id">
                        Account ID {{ selected.accountID }}
                    </div>
                    <div
                        v-if="selected.address1"
                        class="assign-account-selected-row"
                    >
                        <v-icon small color="#78909c" class="mr-1">mdi-map-marker</v-icon>
                        {{ selected.address1 }}
                    </div>
                    <div
                        v-if="selected.homePhone || selected.cellPhone"
                        class="assign-account-selected-row"
                    >
                        <v-icon small color="#78909c" class="mr-1">mdi-phone</v-icon>
                        {{ formatPhone(selected.homePhone) }}
                        <span v-if="selected.homePhone && selected.cellPhone"> · </span>
                        {{ formatPhone(selected.cellPhone) }}
                    </div>
                    <div
                        v-if="selected.emailAddress"
                        class="assign-account-selected-row"
                    >
                        <v-icon small color="#78909c" class="mr-1">mdi-email-outline</v-icon>
                        {{ selected.emailAddress }}
                    </div>
                </div>
            </div>
        </v-card>
    </div>
</template>
<script>
import { formatPhoneNumber } from "../../utils/helpers.js";

export default {
    name: "AssignAccountLookup",
    props: {
        value: String,
        searchUrl: {
            type: String,
            default: "api/accounts/searchAccounts",
        },
        extraParams: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            search: "",
            loading: false,
            results: [],
            selected: null,
            searchTimer: null,
        };
    },
    methods: {
        onSearch() {
            // Typing a new search means the user wants a different account, so
            // drop the current pick and bring the results list back.
            if (this.selected) {
                this.removeSelected();
            }

            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }

            this.searchTimer = setTimeout(() => {
                this.loadResults();
            }, 300);
        },
        async loadResults() {
            const value = (this.search || "").trim();
            if (value.length < 2) {
                this.results = [];
                return;
            }

            this.loading = true;

            try {
                const params = Object.assign({ value }, this.extraParams || {});
                const query = Object.keys(params)
                    .filter(
                        (k) =>
                            params[k] !== undefined &&
                            params[k] !== null &&
                            params[k] !== ""
                    )
                    .map(
                        (k) =>
                            encodeURIComponent(k) +
                            "=" +
                            encodeURIComponent(params[k])
                    )
                    .join("&");

                const results = await this.$http.get(
                    this.searchUrl + "?" + query
                );
                this.results = results || [];
            } catch (e) {
                this.results = [];
            }

            this.loading = false;
        },
        select(account) {
            this.selected = account;
            this.$emit("input", account.accountID);
            this.$emit("selected", account);
        },
        formatPhone(value) {
            return value ? formatPhoneNumber(value) : "";
        },
        removeSelected() {
            this.selected = null;
            this.$emit("input", "");
            this.$emit("selected", null);
        },
        clearSelection() {
            this.selected = null;
        },
        clear() {
            this.search = "";
            this.results = [];
            this.selected = null;
            this.$emit("input", "");
            this.$emit("selected", null);
        },
    },
};
</script>
<style>
.assign-account-results {
    max-height: 280px;
    overflow-y: auto;
}

.assign-account-card {
    cursor: pointer;
    border: 1px solid #e0e0e0;
    transition: border-color 0.15s ease, background 0.15s ease;
}

.assign-account-card:hover {
    border-color: #bdbdbd;
    background: #fafafa;
}

.assign-account-card--selected {
    border-color: #4caf50;
    background: #f1ffdc;
}

.assign-account-card-icon {
    flex: 0 0 auto;
    margin-top: 2px;
}

.assign-account-card-body {
    flex: 1 1 auto;
    min-width: 0;
}

.assign-account-selected {
    border: none;
    border-left: 4px solid #4caf50;
    background: #f4fff4;
    box-shadow: 0 2px 6px rgba(76, 175, 80, 0.18);
    overflow: hidden;
}

.assign-account-selected-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0px 6px 0 12px;
}

.assign-account-selected-clear {
    margin: 0;
    height: 28px;
    width: 28px;
}

.assign-account-selected-main {
    display: flex;
    align-items: flex-start;
    padding: 4px 12px 10px;
}

.assign-account-selected-icon {
    flex: 0 0 auto;
    margin-right: 10px;
    margin-top: 1px;
}

.assign-account-selected-body {
    flex: 1 1 auto;
    min-width: 0;
}

.assign-account-selected-label {
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-size: 11px;
    font-weight: 700;
    color: #2e7d32;
}

.assign-account-selected-name {
    font-size: 15px;
    font-weight: 600;
    color: #263238;
    line-height: 1.3;
}

.assign-account-selected-he {
    margin-left: 6px;
    font-weight: 400;
    color: #78909c;
}

.assign-account-selected-id {
    color: #607d8b;
    font-size: 12px;
    font-weight: 500;
    margin-top: 2px;
}

.assign-account-selected-row {
    display: flex;
    align-items: center;
    margin-top: 3px;
    font-size: 13px;
    color: #37474f;
}
</style>
